import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../../store/languageSlice';
import { cvData } from '../../data/cvData';
import { translations } from '../../data/translations';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initHeroNameScroll } from './heroNameScroll';
import HeroMonitorScene from './HeroMonitorScene';
import './Hero.css';

gsap.registerPlugin(ScrollTrigger);

// ── GlitchText — RGB split + clip-path burst ─────────────────────────────────
const GlitchText = ({ children, style }) => {
  const containerRef = useRef(null);
  const tidRef = useRef(null);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const [L0, L1] = Array.from(c.querySelectorAll('[data-gl]'));

    const fire = () => {
      gsap.timeline({
        onComplete: () => {
          gsap.set([L0, L1], { x: 0, skewX: 0 });
          gsap.set(c, { clipPath: 'inset(0% 0 0% 0)' });
          tidRef.current = setTimeout(fire, 3500 + Math.random() * 5000);
        },
      })
        .to(L0, { x: -10, skewX: 4, duration: 0.05, ease: 'none' }, 0)
        .to(L0, { x: 6, skewX: -2, duration: 0.04, ease: 'none' })
        .to(L0, { x: 0, skewX: 0, duration: 0.05 })
        .to(L1, { x: 8, skewX: -3, duration: 0.05, ease: 'none' }, 0.02)
        .to(L1, { x: -4, duration: 0.04, ease: 'none' })
        .to(L1, { x: 0, skewX: 0, duration: 0.05 })
        .to(c, { clipPath: `inset(${15 + Math.random() * 25}% 0 ${15 + Math.random() * 25}% 0)`, duration: 0.05 }, 0.1)
        .to(c, { clipPath: 'inset(0% 0 0% 0)', duration: 0.04 })
        .to(L0, { x: 5, duration: 0.03, ease: 'none' }, '+=0.04')
        .to(L0, { x: 0, duration: 0.04 });
    };

    tidRef.current = setTimeout(fire, 1200 + Math.random() * 2000);
    return () => {
      clearTimeout(tidRef.current);
      gsap.killTweensOf([L0, L1, c]);
    };
  }, [children]);

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', clipPath: 'inset(0% 0 0% 0)', ...style }}>
      <span data-name-text style={{ position: 'relative', zIndex: 3 }}>{children}</span>
      <span data-name-text data-gl="0" aria-hidden style={{ position: 'absolute', inset: 0, color: '#00e5cc', opacity: 0.5, mixBlendMode: 'screen', zIndex: 1, pointerEvents: 'none' }}>{children}</span>
      <span data-name-text data-gl="1" aria-hidden style={{ position: 'absolute', inset: 0, color: '#FE5B00', opacity: 0.5, mixBlendMode: 'screen', zIndex: 2, pointerEvents: 'none' }}>{children}</span>
    </div>
  );
};

// ── Hero ─────────────────────────────────────────────────────────────────────
const Hero = () => {
  const language = useSelector(selectLanguage);
  const data = cvData[language];
  const t = translations[language];
  const { personal } = data;

  const wrapRef = useRef(null);
  const heroRef = useRef(null);
  const nameContainerRef = useRef(null);
  const nameLineRefs = useRef([]);
  const monitorSceneRef    = useRef(null);   // contenitore CSS del monitor
  const monitorProgressRef = useRef(0);      // 0→1 condiviso con HeroMonitorScene

  const scrollToContact = () => document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToAbout   = () => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });

  const nameParts = personal.name.split(' ');

  // Staggered reveal animation on mount
  useEffect(() => {
    const els = wrapRef.current?.querySelectorAll('[data-anim]');
    if (!els) return;
    gsap.fromTo(els,
      { y: 36, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.12, ease: 'power3.out', delay: 0.15 }
    );
  }, []);

  // Nome hero → E P in header (pin + animazione, poi scroll libero)
  useEffect(() => {
    const hero = heroRef.current;
    const nameContainer = nameContainerRef.current;
    const headerSlot = document.getElementById('header-logo-slot');
    const nameLines = nameLineRefs.current.filter(Boolean);

    if (!hero || !nameContainer || !headerSlot || nameLines.length < 2) return undefined;

    let cleanupFn = () => {};
    const monitorEl = monitorSceneRef.current;
    let targetX = 0;

    // Calcola targetX una volta che il layout è stabile
    const rafId = requestAnimationFrame(() => {
      if (monitorEl) {
        const rect = monitorEl.getBoundingClientRect();
        targetX = window.innerWidth / 2 - (rect.left + rect.width / 2);
      }
    });

    const timerId = window.setTimeout(() => {
      cleanupFn = initHeroNameScroll({
        heroSection: hero,
        nameContainer,
        nameLines,
        headerSlot,
        names: nameParts,
        // Callback chiamato ad ogni aggiornamento di progresso dal nome scroll
        onMonitorProgress: (monitorP) => {
          monitorProgressRef.current = monitorP;
          if (monitorEl) {
            monitorEl.style.transform =
              monitorP > 0 ? `translateX(${targetX * monitorP}px)` : '';
          }
        },
      });
      ScrollTrigger.refresh();
    }, 1000);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
      cleanupFn();
      if (monitorEl) monitorEl.style.transform = '';
      monitorProgressRef.current = 0;
    };
  }, [nameParts.join(' ')]);

  const nameStyle = {
    fontFamily: '"Doto", sans-serif',
    fontOpticalSizing: 'auto',
    fontVariationSettings: '"ROND" 0',
    fontSize: 'clamp(3.8rem, 10.5vw, 10rem)',
    fontWeight: 700,
    lineHeight: 0.9,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    textTransform: 'uppercase',
    display: 'block',
  };

  return (
    <section
      id="hero"
      ref={heroRef}
      style={{
        minHeight: '100vh',
        paddingTop: '70px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div
        ref={wrapRef}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          maxWidth: '1280px',
          width: '100%',
          margin: '0 auto',
          padding: '0 clamp(1.5rem, 4vw, 4rem)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* ── Greeting label ── */}
        <p
          data-anim
          style={{
            fontFamily: 'monospace',
            fontSize: '0.72rem',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'var(--text-accent)',
            marginBottom: '1.25rem',
            opacity: 0,
          }}
        >
          {t.hero.greeting} —
        </p>

        {/* ── Nome + scena 3D ── */}
        <div
          data-anim
          className="hero-name-row"
          style={{ opacity: 0 }}
        >
          {/* Nome (sinistra) */}
          <div className="hero-name-row__name" ref={nameContainerRef}>
            {nameParts.map((word, i) => (
              <div
                key={word}
                ref={(el) => {
                  nameLineRefs.current[i] = el;
                }}
                className="hero-name-line"
                style={{ marginBottom: i < nameParts.length - 1 ? '0.04em' : 0 }}
              >
                <GlitchText style={nameStyle}>{word}</GlitchText>
              </div>
            ))}
          </div>

          {/* Scena 3D (destra) */}
          <div
            ref={monitorSceneRef}
            className="hero-name-row__scene"
            aria-hidden="true"
          >
            <HeroMonitorScene progressRef={monitorProgressRef} />
          </div>
        </div>

        {/* ── Divider + availability pill ── */}
        <div
          data-anim
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.25rem',
            margin: '2rem 0 2rem',
            opacity: 0,
          }}
        >
          <div style={{ flex: 1, height: '1px', background: 'var(--border-accent-alpha)' }} />
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#2ecc71',
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              flexShrink: 0,
            }}
          >
            <span style={{ animation: 'hero-pulse 2s ease-in-out infinite' }}>●</span>
            available
          </span>
        </div>

        {/* ── Role + description  ↔  CTAs ── */}
        <div
          data-anim
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) auto',
            gap: 'clamp(1.5rem, 4vw, 4rem)',
            alignItems: 'flex-end',
            opacity: 0,
          }}
        >
          {/* Left: role + description */}
          <div>
            <h2
              style={{
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--text-accent)',
                marginBottom: '0.75rem',
              }}
            >
              {personal.title}
            </h2>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: 'clamp(0.9rem, 1.3vw, 1.05rem)',
                lineHeight: 1.75,
                maxWidth: '500px',
                fontFamily: 'monospace',
                letterSpacing: '0.01em',
              }}
            >
              {t.hero.subtitle}
            </p>
          </div>

          {/* Right: CTAs stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'flex-end' }}>
            <button
              onClick={scrollToContact}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--button-primary-bg)',
                color: 'var(--button-primary-text)',
                border: 'none',
                fontSize: '0.72rem',
                fontWeight: 600,
                fontFamily: 'monospace',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: 'var(--shadow-glow)',
                transition: 'background-color 150ms, box-shadow 150ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--button-primary-hover)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--button-primary-bg)';
                e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
              }}
            >
              {language === 'it' ? '> contattami' : '> contact_me'}
            </button>

            <button
              onClick={scrollToAbout}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                color: 'var(--button-secondary-text)',
                border: '1px solid var(--border-accent-alpha)',
                fontSize: '0.72rem',
                fontWeight: 400,
                fontFamily: 'monospace',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background-color 150ms, border-color 150ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--button-secondary-hover)';
                e.currentTarget.style.borderColor = 'var(--border-accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'var(--border-accent-alpha)';
              }}
            >
              {t.hero.cta}
            </button>
          </div>
        </div>

        {/* ── Scroll indicator ── */}
        <div
          data-anim
          style={{ marginTop: 'clamp(2rem, 4vh, 3.5rem)', opacity: 0 }}
        >
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.62rem',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
            }}
          >
            scroll ↓
          </span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
